import moment from 'moment';
import React, { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import parsePhoneNumber from 'libphonenumber-js';
import * as Sentry from "@sentry/react";
import { isRaccoonError } from '@myhealth-raccoon/services';

import { PersonalDetailsForm, PersonalDetailsFormValues } from 'src/components/forms/PersonalDetailsForm';
import { useBookingFlowStep } from 'src/hooks/useBookingFlowStep';
import { useCancelReservedBooking } from 'src/hooks/useCancelReservedBooking';
import { authService } from 'src/services/auth';
import { useDispatch, useSelector } from 'src/store';
import { bookingFlowSlice } from 'src/store/bookingFlow';
import { ContentContainer } from 'src/components/ContentContainer';
import { isRequestError } from 'src/utils/requestError';

export function PersonalDetails() {
    useBookingFlowStep(1);
    useCancelReservedBooking();
    const navigate = useNavigate();
    const clinicId = useSelector(state => state.appointment.clinicId);
    const dispatch = useDispatch();

    const onSubmit = useCallback(async ({ dateOfBirth, firstName, lastName, mobileNumber }: PersonalDetailsFormValues) => {
        try {
            const { tokenResponse: _, ...verificationInfo } = await authService.Intitial({
                dob: moment(new Date(dateOfBirth)).format('DD-MM-YYYY'),
                firstname: firstName.trim(),
                surname: lastName.trim(),
                mobile: parsePhoneNumber(mobileNumber, "AU")!.formatNational().replace(/\s*/g, ""),
                siteId: clinicId
            });
            dispatch(bookingFlowSlice.actions.setVerificationInfo(verificationInfo));
            dispatch(bookingFlowSlice.actions.setPatientName(`${firstName} ${lastName}`))
            dispatch(bookingFlowSlice.actions.setPatientDob(dateOfBirth));
            navigate('/app/verification');
        } catch (e) {
            if (e instanceof Error) {
                Sentry.captureMessage(`PatientDetails Verify Error. ${e.message || e.name}`);
                if (isRequestError(e) && isRaccoonError(e.data)) {
                    if (e.data?.messages.find(e => e.value.includes("patient is not found"))) {
                        navigate("/app/patient-not-found");
                        return;
                    }
                    if (e.data?.messages.find(e => e.value.includes("patient claims to be existing"))) {
                        navigate("/app/patient-not-found");
                        return;
                    }
                    if (e.data?.messages.find(m => m.value.includes("Banned Level 2"))) {
                        navigate('/app/err-unable-sched-appt', { replace: true, state: { bannedLevel: "2" } });
                        return;
                    }
                    if (e.data?.messages.find(m => m.value.includes("Banned"))) {
                        navigate('/app/err-unable-sched-appt', { replace: true, state: { bannedLevel: "1" } });
                        return;
                    }
                }
                Sentry.captureException(e);
                toast.error("An error occurred");
            }
        }
    }, [clinicId, dispatch, navigate]);

    return <>
        <ContentContainer className='text-base'>
            <h1 className='text-2xl text-gray-900 font-semibold mb-2.5'>Please enter your details</h1>
            <p>
                Provide personal details below to book an appointment.
                If you're booking on behalf of someone else, enter their details instead.
            </p>
            <p><br /></p>
            <p aria-hidden><span className='text-primary'>*</span> indicates a required field</p>

        </ContentContainer>
        <PersonalDetailsForm className='mt-8 flex-1'
            onSubmit={onSubmit}
            onBack={() => navigate(-1)}
        />
    </>
}